import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import './SquarePaymentForm.css';

import { environment } from '../../../environments/environment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import visa from '../../../assets/visa.png';
import amex from '../../../assets/amex.png';
import discover from '../../../assets/dicover.png';
import dinars from '../../../assets/dinars.png';
import geniric from '../../../assets/geniric.png';
import jbc from '../../../assets/geniric.png';
import mastercard from '../../../assets/mastercard.png';
import unionpay from '../../../assets/unionpay.png';

const SCRIPT_URL = environment.squareScriptUrl;
// Height for the Square card container (px)
const CARD_CONTAINER_MIN_HEIGHT = 72;
const SquarePaymentForm = forwardRef(function SquarePaymentForm(
  {
    applicationId = environment.squareApplicationId,
    locationId,
    onToken,
    onError,
    savedCards = [],
    selectedSavedCardId,
    onSavedCardSelect,
    shouldSaveCard,
    onSaveCardChange,
    useNewCard,
    onUseNewCardChange,
  },
  ref
) {
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const [ready, setReady] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isLoadingCard, setIsLoadingCard] = React.useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    React.useState('card'); // only 'card' supported now
  const isInitializedRef = useRef(false); // Track if card has been initialized
  const containerIdRef = useRef('sq-card-container'); // Fixed container ID

  useEffect(() => {
    let mounted = true;

    // Prevent duplicate initialization
    if (isInitializedRef.current && cardRef.current) {
      return;
    }

    const loadScript = async () => {
      if (!locationId) {
        onError?.(new Error('Square locationId is required'));
        return;
      }

      if (!window.Square) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = SCRIPT_URL;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        }).catch((err) => {
          onError?.(err);
        });
      }

      if (!mounted) {
        return;
      }

      try {
        const payments = window.Square.payments(applicationId, locationId);
        paymentsRef.current = payments;

        const container = document.getElementById(containerIdRef.current);
        if (!container) {
          throw new Error('Card container element not found');
        }

        const card = await payments.card();
        await card.attach(`#${containerIdRef.current}`);

        cardRef.current = card;
        isInitializedRef.current = true;

        // Card is initialized once — always clear loading regardless of which
        // payment option is currently selected. The container is toggled via
        // CSS (display:none/block) so Square's iframe stays in the DOM.
        setIsLoadingCard(false);
        setReady(true);
      } catch (err) {
        onError?.(err);
      }
    };

    loadScript();

    return () => {
      mounted = false;

      if (cardRef.current) {
        try {
          cardRef.current.destroy();
          cardRef.current = null;
          isInitializedRef.current = false;
        } catch (e) {
          // Error handled silently
        }
      }
    };
    // Only re-run when locationId or applicationId actually change — NOT when
    // useNewCard/savedCards change. Those only control visibility via CSS and
    // must not trigger a destroy+reinit cycle that can leave the form blank.
  }, [applicationId, locationId, onError]);

  useImperativeHandle(
    ref,
    () => ({
      tokenize: async () => {
        setIsProcessing(true);

        try {
          let result;

          if (selectedPaymentMethod === 'card') {
            if (!cardRef.current) throw new Error('Card not initialized');
            result = await cardRef.current.tokenize();
          } else {
            throw new Error('No supported payment method selected');
          }

          setIsProcessing(false);

          if (result.status === 'OK') {
            onToken?.(result.token);
            return result.token;
          }

          const message =
            (result.errors && result.errors[0] && result.errors[0].message) ||
            'Tokenization failed';
          const err = new Error(message);
          onError?.(err);
          throw err;
        } catch (error) {
          setIsProcessing(false);
          onError?.(error);
          throw error;
        }
      },
      isReady: () => ready,
      getSelectedPaymentMethod: () => selectedPaymentMethod,
    }),
    [selectedPaymentMethod, ready, onToken, onError]
  );

  // Count visible card inputs for user debugging
  useEffect(() => {
    const checkVisibleInputs = () => {
      const containers = document.querySelectorAll('[id^="sq-card-container"]');
      const visibleContainers = Array.from(containers).filter((el) => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });

      // Duplicate detection handled silently
      if (visibleContainers.length > 1) {
        // Duplicate inputs detected
      }
    };

    // Check after a short delay to allow DOM to settle
    const timer = setTimeout(checkVisibleInputs, 100);
    return () => clearTimeout(timer);
  }, [useNewCard, isLoadingCard]);

  // Function to manually send payment to backend
  if (!applicationId || !locationId) {
    return (
      <Container style={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error">
          Square payment configuration is missing. Please check your environment
          settings.
        </Typography>
      </Container>
    );
  }

  return (
    <Container style={{ padding: 0 }}>
      <Box>
        {/* Saved Cards Section */}
        {savedCards && savedCards.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: 16,
                fontWeight: 600,
                color: '#1A2341',
                mb: 2,
              }}
            >
              Saved Cards ({savedCards.length})
            </Typography>

            {savedCards.map((card) => {
              const isSelected =
                selectedSavedCardId === card.cardId && !useNewCard;
              const cardBrandImage =
                card.brand === 'AMERICAN_EXPRESS'
                  ? amex
                  : card.brand === 'DINERS_CLUB'
                  ? dinars
                  : card.brand === 'DISCOVER'
                  ? discover
                  : card.brand === 'JCB'
                  ? jbc
                  : card.brand === 'VISA'
                  ? visa
                  : card.brand === 'MASTERCARD'
                  ? mastercard
                  : card.brand === 'UNIONPAY'
                  ? unionpay
                  : geniric;

              return (
                <Box
                  key={card.cardId}
                  onClick={() => {
                    onSavedCardSelect?.(card.cardId);
                    onUseNewCardChange?.(false);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.06)',
                    padding: '18px 24px',
                    marginBottom: '12px',
                    border: isSelected
                      ? `2px solid ${environment.mainColor}`
                      : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: environment.mainColor,
                    },
                  }}
                >
                  <img
                    src={cardBrandImage}
                    alt={card.brand}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      marginRight: 18,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        fontSize: 17,
                        color: '#1A2341',
                        letterSpacing: '0.01em',
                      }}
                    >
                      •••• {card.last4}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontSize: 13,
                        color: '#7A869A',
                        marginTop: '2px',
                      }}
                    >
                      {card.cardholderName} • Expires {card.expMonth}/
                      {card.expYear}
                    </Typography>
                  </Box>
                  {isSelected ? (
                    <CheckCircleIcon
                      sx={{ color: environment.mainColor, fontSize: 28 }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ color: '#E0E0E0', fontSize: 28 }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Pay with New Card Option */}
        <Box
          onClick={() => {
            onUseNewCardChange?.(true);
            setSelectedPaymentMethod('card');
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.06)',
            padding: '18px 24px',
            marginBottom: '12px',
            border: useNewCard
              ? `2px solid ${environment.mainColor}`
              : '2px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: environment.mainColor,
            },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              backgroundColor: environment.mainColor,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '18px',
            }}
          >
            <svg
              width="20"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="20" height="16" rx="2" fill="white" />
              <rect x="2" y="4" width="16" height="2" fill="#666" />
              <rect x="2" y="8" width="6" height="1" fill="#666" />
              <rect x="2" y="10" width="4" height="1" fill="#666" />
            </svg>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                fontSize: 17,
                color: '#1A2341',
                letterSpacing: '0.01em',
              }}
            >
              Pay with New Card
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: 13,
                color: '#7A869A',
                marginTop: '2px',
              }}
            >
              Secure payment powered by Square
            </Typography>
          </Box>
          {useNewCard ? (
            <CheckCircleIcon
              sx={{ color: environment.mainColor, fontSize: 28 }}
            />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: '#E0E0E0', fontSize: 28 }} />
          )}
        </Box>

        {/* Card Payment Form - Always render container for Square initialization */}
        <Box
          sx={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 12px 0 rgba(16, 30, 54, 0.06)',
            padding: '24px',
            marginBottom: '16px',
            display: useNewCard ? 'block' : 'none',
          }}
        >
          {/* Mount Square card element here */}
          <Box
            sx={{
              position: 'relative',
              minHeight: CARD_CONTAINER_MIN_HEIGHT,
              marginBottom: 2,
            }}
          >
            {/* Always render the card container for Square to attach */}
            <div
              id={containerIdRef.current}
              style={{
                minHeight: CARD_CONTAINER_MIN_HEIGHT,
              }}
            />

            {/* Loader overlay while card initializes */}
            {isLoadingCard && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress
                    size={32}
                    style={{ color: environment.mainColor, marginBottom: 8 }}
                  />
                  <Typography
                    style={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: 14,
                      color: '#7A869A',
                    }}
                  >
                    Loading secure payment form...
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Save Card Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={shouldSaveCard}
                onChange={(e) => onSaveCardChange?.(e.target.checked)}
                sx={{
                  color: '#E0E0E0',
                  '&.Mui-checked': {
                    color: environment.mainColor,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: 14,
                  color: '#1A2341',
                }}
              >
                Save this card for future purchases
              </Typography>
            }
          />
        </Box>

        {/* Cash App Pay Option */}
        {/* Cash App Pay option removed */}

        {/* Processing Overlay */}
        {isProcessing && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                padding: '32px',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '300px',
              }}
            >
              <CircularProgress
                size={48}
                style={{ color: environment.mainColor, marginBottom: '16px' }}
              />
              <Typography
                style={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1A2341',
                  textAlign: 'center',
                  marginBottom: '8px',
                }}
              >
                {'Processing Payment...'}
              </Typography>
              <Typography
                style={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: 14,
                  color: '#7A869A',
                  textAlign: 'center',
                }}
              >
                Please wait while we securely process your payment
              </Typography>
            </div>
          </div>
        )}
      </Box>
    </Container>
  );
});

export default SquarePaymentForm;
